/**
 * Contraste des couleurs de marque (WCAG 2.x), partagé par l'API (e-mails, icônes) et l'interface :
 * un même calcul pour que le bouton d'un e-mail et celui de l'application aient la même couleur de texte.
 * Fonctions pures, sans dépendance au navigateur.
 */

type Rgb = [number, number, number];

function parseHex(hex: string): Rgb {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h.slice(0, 6);
  const rgb = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return (rgb.some((v) => Number.isNaN(v)) ? [0, 0, 0] : rgb) as Rgb;
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b]
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Luminance relative WCAG 2.x (0 à 1). */
export function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map(channel) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Rapport de contraste WCAG entre deux couleurs (1 à 21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Couleur de texte la plus lisible sur un fond donné (noir ou blanc, contraste WCAG le plus élevé). */
export function readableOn(hex: string): '#000000' | '#ffffff' {
  return contrastRatio(hex, '#000000') >= contrastRatio(hex, '#ffffff') ? '#000000' : '#ffffff';
}

/**
 * Couleur de marque lisible sur fond clair : même teinte, assombrie par pas jusqu'à atteindre `min`
 * (4,5:1 par défaut, AA texte courant) sur `surface`.
 */
export function strongOn(hex: string, surface = '#ffffff', min = 4.5): string {
  let rgb = parseHex(hex);
  for (let i = 0; i < 40 && contrastRatio(toHex(rgb), surface) < min; i++) {
    rgb = rgb.map((v) => v * 0.92) as Rgb;
  }
  return toHex(rgb);
}
